import { createReducer, on } from '@ngrx/store';

import * as TodoActions from './todo.actions';
import type { TodoItem, TodoState } from './todo.models';

export const initialState: TodoState = {
  items: [],
  loading: false,
  saving: false,
  error: null,
  message: null,
  success: false,
};

function sortTodos(items: TodoItem[]): TodoItem[] {
  return [...items].sort((left, right) => {
    if (left.is_completed !== right.is_completed) {
      return left.is_completed ? 1 : -1;
    }
    if (left.position !== right.position) {
      return left.position - right.position;
    }
    return right.created_at.localeCompare(left.created_at);
  });
}

function upsertTodo(items: TodoItem[], item: TodoItem): TodoItem[] {
  return sortTodos([item, ...items.filter((existing) => existing.id !== item.id)]);
}

function moveTodo(items: TodoItem[], todoId: string, direction: 'up' | 'down'): TodoItem[] {
  const sortedItems = sortTodos(items);
  const target = sortedItems.find((item) => item.id === todoId);
  if (!target) {
    return sortedItems;
  }

  const group = sortedItems.filter((item) => item.is_completed === target.is_completed);
  const index = group.findIndex((item) => item.id === todoId);
  if (index === -1) {
    return sortedItems;
  }

  const swapIndex = direction === 'up' ? index - 1 : index + 1;
  if (swapIndex < 0 || swapIndex >= group.length) {
    return sortedItems;
  }

  const nextGroup = [...group];
  const current = { ...nextGroup[index] };
  const swap = { ...nextGroup[swapIndex] };
  const currentPosition = current.position;
  current.position = swap.position;
  swap.position = currentPosition;
  nextGroup[index] = swap;
  nextGroup[swapIndex] = current;

  return sortTodos([
    ...sortedItems.filter((item) => item.is_completed !== target.is_completed),
    ...nextGroup,
  ]);
}

function reorderTodo(items: TodoItem[], todoId: string, targetPosition: number): TodoItem[] {
  const sortedItems = sortTodos(items);
  const target = sortedItems.find((item) => item.id === todoId);
  if (!target) {
    return sortedItems;
  }

  const group = sortedItems.filter((item) => item.is_completed === target.is_completed);
  const index = group.findIndex((item) => item.id === todoId);
  if (index === -1) {
    return sortedItems;
  }

  const boundedIndex = Math.max(0, Math.min(group.length - 1, targetPosition - 1));
  if (boundedIndex === index) {
    return sortedItems;
  }

  const nextGroup = [...group];
  const [moved] = nextGroup.splice(index, 1);
  nextGroup.splice(boundedIndex, 0, moved);

  const normalizedGroup = nextGroup.map((item, itemIndex) => ({
    ...item,
    position: itemIndex + 1,
  }));

  return sortTodos([
    ...sortedItems.filter((item) => item.is_completed !== target.is_completed),
    ...normalizedGroup,
  ]);
}

export const todoReducer = createReducer(
  initialState,

  on(TodoActions.TodoClearStatus, (state) => ({
    ...state,
    error: null,
    message: null,
    success: false,
  })),

  on(TodoActions.TodoItemsFetchRequest, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(TodoActions.TodoItemsFetchSuccess, (state, { items }) => ({
    ...state,
    items: sortTodos(items),
    loading: false,
    error: null,
  })),
  on(TodoActions.TodoItemsFetchFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
    success: false,
  })),

  on(TodoActions.TodoCreateRequest, TodoActions.TodoUpdateRequest, TodoActions.TodoReorderRequest, TodoActions.TodoDeleteRequest, (state) => ({
    ...state,
    saving: true,
    error: null,
    success: false,
  })),

  on(TodoActions.TodoCreateSuccess, (state, { item }) => ({
    ...state,
    items: upsertTodo(state.items, item),
    saving: false,
    error: null,
    success: true,
  })),
  on(TodoActions.TodoUpdateRequest, (state, { todoId, payload }) => ({
    ...state,
    items: state.items.map((item) =>
      item.id === todoId
        ? {
            ...item,
            ...payload,
            completed_at: payload.is_completed === undefined
              ? item.completed_at
              : (payload.is_completed ? new Date().toISOString() : null),
            updated_at: new Date().toISOString(),
          }
        : item,
    ),
    saving: true,
    error: null,
    success: false,
  })),
  on(TodoActions.TodoUpdateSuccess, (state, { item }) => ({
    ...state,
    items: upsertTodo(state.items, item),
    saving: false,
    error: null,
    success: true,
  })),
  on(TodoActions.TodoReorderRequest, (state, { todoId, payload }) => ({
    ...state,
    items: payload.target_position !== undefined
      ? reorderTodo(state.items, todoId, payload.target_position)
      : (payload.direction ? moveTodo(state.items, todoId, payload.direction) : state.items),
    saving: true,
    error: null,
    success: false,
  })),
  on(TodoActions.TodoReorderSuccess, (state, { items }) => ({
    ...state,
    items: sortTodos(items),
    saving: false,
    error: null,
    success: true,
  })),
  on(TodoActions.TodoDeleteSuccess, (state, { todoId }) => ({
    ...state,
    items: state.items.filter((item) => item.id !== todoId),
    saving: false,
    error: null,
    success: true,
  })),

  on(TodoActions.TodoUpdateFailure, (state, { error, previousItem }) => ({
    ...state,
    items: upsertTodo(state.items, previousItem),
    saving: false,
    error,
    success: false,
  })),
  on(TodoActions.TodoReorderFailure, (state, { error, previousItems }) => ({
    ...state,
    items: sortTodos(previousItems),
    saving: false,
    error,
    success: false,
  })),
  on(TodoActions.TodoCreateFailure, TodoActions.TodoUpdateFailure, TodoActions.TodoDeleteFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
    success: false,
  })),
);
